import type { ReactElement, ReactNode } from "react"
import { Children, createElement, isValidElement } from "react"
import NestedLinkAtm from "@onestore/hel/dist/components/atoms/NestedLinkAtm"
import ListMol from "@onestore/hel/dist/components/molecules/ListMol"
import Paragraph from "@gatsby-plugin-generic-page/markdown/components/Paragraph"
import { Picture, Text } from "@ionos-web-design-system/react"
import type { Components } from "react-markdown"

const markdownOverrides: Components = {
  h1: ({ children }: { children: ReactNode }) => (
    <Text asChild variant="heading2xl" weight="bold" className="mb-8">
      <h1>{children}</h1>
    </Text>
  ),
  h2: ({ children }: { children: ReactNode }) => (
    <Text asChild variant="heading2xl" weight="bold" className="mb-8">
      <h2>{children}</h2>
    </Text>
  ),
  h3: ({ children }: { children: ReactNode }) => (
    <Text asChild variant="heading2xl" weight="bold" className="mb-4">
      <h3>{children}</h3>
    </Text>
  ),
  h4: ({ children }: { children: ReactNode }) => (
    <Text asChild variant="bodyLg" weight="bold" className="mb-2">
      <h4>{children}</h4>
    </Text>
  ),
  h5: ({ children }: { children: ReactNode }) => (
    <Text asChild variant="body" weight="bold" className="mb-2">
      <h5>{children}</h5>
    </Text>
  ),
  h6: ({ children }: { children: ReactNode }) => (
    <Text asChild variant="bodySm" weight="bold" className="mb-2">
      <h6>{children}</h6>
    </Text>
  ),
  p: ({ children }: { children: ReactNode }) => (
    <Paragraph typography="medium1_h" pushSpace={2}>
      {children}
    </Paragraph>
  ),
  span: ({ children }: { children: ReactNode }) => (
    <Text asChild variant="body" className="mb-2">
      <span>{children}</span>
    </Text>
  ),
  ul: ({ children }: { children: ReactNode }) => (
    <div className="my-4">
      <ListMol
        items={Children.toArray(children)
          .filter(isValidElement)
          .map((child) => (
            <Text variant="body">{(child as ReactElement).props.children}</Text>
          ))}
        type="dot"
        size="medium"
      />
    </div>
  ),
  ol: ({ children }: { children: ReactNode }) => (
    <div className="my-4">
      <ListMol
        items={Children.toArray(children)
          .filter(isValidElement)
          .map((child) => (child as ReactElement).props.children)}
        type="number"
        size="medium"
      />
    </div>
  ),
  img: (({
    node,
    ...props
  }: {
    node?: unknown
    src?: string
    alt?: string
  }) => (
    <Picture
      className="my-8"
      srcData={{ ...props, src: props.src ?? "", alt: props.alt ?? "" }}
    />
  )) as Components["img"],
  a: (({ href, children }: { href?: string; children: ReactNode }) => {
    if (
      !href ||
      href.startsWith("mailto:") ||
      /^[^\s/]+@[^\s/]+\.[^\s/]+$/.test(href)
    ) {
      return <>{children}</>
    }
    return createElement(NestedLinkAtm, {
      href,
      openInNewTab: true,
      title: href ?? "",
      children,
    })
  }) as Components["a"],
} as Components

export default markdownOverrides
